// The collections endpoints, in one place.
//
// Instance A owns the routes (backend/app/routes/collections.py); the contract is
// docs/collections.md. Every path is practice-scoped, every amount is integer
// CENTS, and every field name the collections UI reads is named in this file and
// nowhere else. CSRF is attached by the wrapper index.js installs on window.fetch,
// so a plain fetch is correct.
//
// Nothing in this module sends anything on its own. `sendStatement` is called from
// one place — the confirm button of SendStatementModal — and posts back the address
// that dialog showed, never an address a person typed.

const API_URL = process.env.REACT_APP_API_URL || "";

export const BUCKETS = [
  { key: "all", label: "Owes a balance" },
  { key: "due", label: "Due for contact" },
  { key: "refund_due", label: "Refund due" },
];

// practice_patients.payment_type. `self_pay` is the cash-pay mode (ruling R1).
export const PAYMENT_TYPES = [
  { key: "insurance", label: "Insurance" },
  { key: "self_pay", label: "Cash pay" },
  { key: "auto_wc", label: "Auto / workers' comp" },
  { key: "other", label: "Other" },
  { key: "unknown", label: "Unknown" },
];

// patient_contact_service.MANUAL_KINDS — `statement_email` is written only by a send.
export const CONTACT_KINDS = [
  { key: "call", label: "Call" },
  { key: "voicemail", label: "Voicemail" },
  { key: "email", label: "Email" },
  { key: "portal_note", label: "Portal note" },
  { key: "other", label: "Other" },
];

export const CONTACT_OUTCOMES = [
  { key: "reached", label: "Reached" },
  { key: "no_answer", label: "No answer" },
  { key: "promised", label: "Promised to pay" },
  { key: "disputed", label: "Disputed" },
  { key: "paid", label: "Paid" },
  { key: "wrong_number", label: "Wrong number" },
  { key: "other", label: "Other" },
];

const KIND_LABELS = {
  statement_email: "Statement emailed",
  ...Object.fromEntries(CONTACT_KINDS.map((k) => [k.key, k.label])),
};
const OUTCOME_LABELS = Object.fromEntries(
  CONTACT_OUTCOMES.map((o) => [o.key, o.label]),
);

export const kindLabel = (kind) => KIND_LABELS[kind] || kind || "—";
export const outcomeLabel = (outcome) =>
  outcome ? OUTCOME_LABELS[outcome] || outcome : null;
export const paymentTypeLabel = (key) =>
  (PAYMENT_TYPES.find((p) => p.key === key) || {}).label || "Unknown";

// statement_sends.status → what the chip says and how it is coloured.
const SEND_STATUS = {
  sent: { label: "Emailed", tone: "info" },
  opened: { label: "Link opened", tone: "info" },
  paid: { label: "Paid", tone: "good" },
  expired: { label: "Link expired", tone: "muted" },
  bounced: { label: "Bounced", tone: "bad" },
  failed: { label: "Send failed", tone: "bad" },
};

// statement_send_service.AMBIGUOUS_ERRORS: a `failed` row whose error is one of these
// may still have been delivered (the mailer timed out, or the send never finished).
export const AMBIGUOUS_SEND_ERRORS = [
  "send did not complete",
  "ReadTimeout",
  "Timeout",
];

// The server's list when we have it (GET /settings), else the copy above.
export const isAmbiguousSendError = (error, serverList = null) =>
  (Array.isArray(serverList) && serverList.length
    ? serverList
    : AMBIGUOUS_SEND_ERRORS
  ).includes(error);

export function sendStatusChip(status, error = null, serverList = null) {
  if (status === "failed" && isAmbiguousSendError(error, serverList)) {
    // Not "Send failed": a biller who reads that sends again, and the patient may
    // get two.
    return { label: "Unconfirmed", tone: "warn" };
  }
  return SEND_STATUS[status] || { label: status || "Unknown", tone: "muted" };
}

// Never surface a server message we did not write. A returns {error: "..."} for
// the refusals a biller can act on and a generic sentence for anything else.
async function readError(response, fallback) {
  try {
    const body = await response.json();
    return { message: (body && body.error) || fallback, body: body || {} };
  } catch (err) {
    return { message: fallback, body: {} };
  }
}

async function request(url, options, fallback) {
  const response = await fetch(url, { credentials: "include", ...options });
  if (!response.ok) {
    const { message, body } = await readError(response, fallback);
    const error = new Error(message);
    error.status = response.status;
    error.body = body;
    throw error;
  }
  // A 200 is not a payload. A truncated body or a proxy's HTML page parses to
  // null, and a caller defaulting that to [] would render "nobody owes anything"
  // at exactly the moment we could not check.
  const body = await response.json().catch(() => null);
  if (!body || typeof body !== "object") {
    const error = new Error(
      "The response could not be read, so this may be incomplete. Try again.",
    );
    error.status = response.status;
    error.body = {};
    throw error;
  }
  return body;
}

function post(payload) {
  return {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  };
}

const base = (practiceId) =>
  `${API_URL}/api/practices/${practiceId}/collections`;
const patientBase = (practiceId, patientId) =>
  `${base(practiceId)}/patients/${encodeURIComponent(patientId)}`;

const num = (value) => {
  const n = Number(value);
  return value == null || value === "" || !Number.isFinite(n) ? null : n;
};

// Integer cents → the ledger's dollar formatter's input. Kept here so no component
// divides by 100 on its own and one forgets.
export const centsToDollars = (cents) => (cents == null ? null : cents / 100);

// "12.50" → 1250; blank → null; anything unreadable → NaN, which the caller refuses.
export function dollarsToCents(text) {
  // What a person types for an amount: a currency sign, thousands separators and a
  // bare ".50" are all ordinary. Grouping is VALIDATED rather than stripped, because
  // this parser is shared with the charge and refund dialogs: "1,00" is a
  // decimal-comma typo, and stripping commas blindly would turn it into $100.
  const raw = String(text ?? "")
    .trim()
    .replace(/^\$/, "")
    .trim();
  if (!raw) return null;
  if (!/^(\d+|\d{1,3}(,\d{3})+)(\.\d{0,2})?$|^\.\d{1,2}$/.test(raw)) return NaN;
  return Math.round(Number(raw.replace(/,/g, "")) * 100);
}


// ---- worklist ----

// GET …/worklist?bucket&min_balance_cents&payment_type → normalised. Refuses a body
// without a rows array rather than rendering an all-clear.
export async function fetchWorklist(
  practiceId,
  { bucket = "all", minBalanceCents = null, paymentType = null } = {},
) {
  const qs = new URLSearchParams({ bucket });
  if (minBalanceCents != null)
    qs.set("min_balance_cents", String(minBalanceCents));
  if (paymentType) qs.set("payment_type", paymentType);
  const body = await request(
    `${base(practiceId)}/worklist?${qs.toString()}`,
    {},
    "Could not load the collections list.",
  );
  if (!Array.isArray(body.rows)) {
    throw new Error(
      "The response could not be read, so this list may be incomplete. Try again.",
    );
  }
  const counts = body.counts || {};
  return {
    asOf: body.as_of || null,
    rules: normalizeRules(body.rules),
    counts: {
      all: num(counts.all),
      due: num(counts.due),
      refund_due: num(counts.refund_due),
    },
    rows: body.rows.map(normalizeRow),
  };
}

// Ranges are A's (collections_service): out of range on the wire reads as the
// default, and a bad value is refused 400 on PUT. Kept here so the card refuses it
// before the server has to.
export const OVERDUE_DAYS_MIN = 1;
export const OVERDUE_DAYS_MAX = 365;
export const NO_SHOW_FEE_CENTS_MAX = 100000;

export function normalizeRules(raw) {
  const r = raw || {};
  return {
    // Missing reads as the refusing default, the same as the server's.
    chargeNoShowFee: r.charge_no_show_fee === true,
    chargeOverdue: r.charge_overdue === true,
    cadenceDays: num(r.cadence_days),
    // How long after the FIRST statement was sent a balance counts as overdue —
    // not from the date of service (A's docs/collections.md, ruling (r)).
    // A's DEFAULT_RULES, carried here: a response without these keys (a backend that
    // predates #253, or a partial body) must not leave the fields blank — blank fails
    // validation, and that would stop an admin saving ANY rule on this card.
    overdueDays: num(r.overdue_days) ?? 30,
    // The AMOUNT of the practice's no-show fee, 0 meaning it charges none. Whether
    // such a balance may go on a card is `chargeNoShowFee`, deliberately separate:
    // Vortex sets a fee and charges none.
    noShowFeeCents: num(r.no_show_fee_cents) ?? 0,
    noShowExemptSameDayReschedule:
      r.no_show_exempt_same_day_reschedule !== false,
    statementFromName: r.statement_from_name || null,
    replyTo: r.reply_to || null,
  };
}

// A's `statement_state` (collections_service): has a statement ever REACHED this
// patient. The UI never decides what "delivered" means — a bounce does not count,
// and an earlier delivered send followed by a bounce is still `delivered`, so this
// cannot be derived from `last_statement` alone.
export const STATEMENT_STATES = {
  never_sent: {
    label: "Never sent",
    tone: "warn",
    // States the fact, not a permission: `stripe_service.chargeable` does NOT hold a
    // balance back when the clock has not started (an unstarted clock is simply not
    // overdue), so promising "cannot be charged" here would be a claim the server
    // does not keep. Raised with A.
    why: "No statement has reached this patient, so their overdue clock has not started.",
  },
  last_send_bounced: {
    label: "Statement bounced",
    tone: "bad",
    why: "The statement came back undelivered, so nobody has reached this patient and their overdue clock has not started.",
  },
  // A send whose fate the mailer never reported. The server distinguishes it from
  // `never_sent` precisely so the UI does not invite a second statement for one the
  // patient may already have; the wording is A's, verbatim.
  last_send_unconfirmed: {
    label: "Unconfirmed",
    tone: "warn",
    why: "A statement may or may not have been sent — check before sending another",
  },
};

export function statementState(row) {
  // Own property only: a state of "constructor" or "toString" would otherwise return
  // a function and render an empty chip asserting a clock we know nothing about.
  return Object.prototype.hasOwnProperty.call(
    STATEMENT_STATES,
    row.statementState,
  )
    ? STATEMENT_STATES[row.statementState]
    : null;
}

// The overdue clock counts whole days from the delivery date in UTC
// (`stripe_service._days_since_first_statement`), so the date that dates the clock is
// rendered in UTC too — 01:00Z shown as the previous evening locally would be a day
// out on a money figure.
export function formatUtcDay(value) {
  const text = String(value || "");
  return /^\d{4}-\d{2}-\d{2}/.test(text) ? formatDay(text.slice(0, 10)) : "—";
}

export function normalizeRow(raw) {
  const r = raw || {};
  const statement = r.last_statement || null;
  const contact = r.last_contact || null;
  const card = r.card || null;
  return {
    patientId: r.patient_id,
    patientName: r.patient_name || null,
    paymentType: r.payment_type || "unknown",
    selfPay: r.self_pay === true,
    emailOnFile: r.email_on_file === true,
    balanceCents: num(r.balance_cents) ?? 0,
    claimCount: num(r.claim_count) ?? 0,
    oldestDos: r.oldest_dos || null,
    refundDueCents: num(r.refund_due_cents) ?? 0,
    lastStatement: statement
      ? {
          id: statement.id,
          sentAt: statement.sent_at || null,
          status: statement.status || null,
          error: statement.error || null,
          balanceCents: num(statement.balance_cents),
        }
      : null,
    lastContact: contact
      ? {
          at: contact.at || null,
          kind: contact.kind || null,
          outcome: contact.outcome || null,
          byUser: contact.by_user || null,
        }
      : null,
    nextContactOn: r.next_contact_on || null,
    // Not derived from last_statement: see STATEMENT_STATES.
    statementState: r.statement_state || null,
    nextAction: r.next_action || null,
    // When the overdue clock started, which is what `overdue_days` counts from.
    firstDeliveredSendAt: r.first_delivered_send_at || null,
    dueForContact: r.due_for_contact === true,
    dueReason: r.due_reason || null,
    card: card
      ? {
          brand: card.card_brand || null,
          last4: card.card_last4 || null,
          exp: card.card_exp || null,
          consentedAt: card.consented_at || null,
          consentTextVersion: num(card.consent_text_version),
        }
      : null,
  };
}

// ---- contact log ----

export async function fetchContacts(practiceId, patientId) {
  const body = await request(
    `${patientBase(practiceId, patientId)}/contacts`,
    {},
    "Could not load the contact log.",
  );
  if (!Array.isArray(body.contacts)) {
    throw new Error(
      "The response could not be read, so the contact log may be incomplete. Try again.",
    );
  }
  return body.contacts.map(normalizeContact);
}

// POST …/contacts {kind, outcome?, note?, next_contact_on?} → the new contact.
export async function addContact(
  practiceId,
  patientId,
  { kind, outcome, note, nextContactOn },
) {
  const payload = { kind };
  if (outcome) payload.outcome = outcome;
  if (note && note.trim()) payload.note = note.trim();
  if (nextContactOn) payload.next_contact_on = nextContactOn;
  const body = await request(
    `${patientBase(practiceId, patientId)}/contacts`,
    post(payload),
    "Could not log the contact.",
  );
  return body.contact ? normalizeContact(body.contact) : null;
}

export function normalizeContact(raw) {
  const c = raw || {};
  return {
    id: c.id,
    patientId: c.patient_id,
    kind: c.kind || null,
    outcome: c.outcome || null,
    note: c.note || null,
    byUser: c.by_user || null,
    at: c.at || null,
    nextContactOn: c.next_contact_on || null,
  };
}

// ---- statement send ----

// GET …/statement/preview — what the confirm shows. `toEmail` is shown exactly and
// is the only value `sendStatement` may post back.
export async function fetchStatementPreview(practiceId, patientId) {
  const body = await request(
    `${patientBase(practiceId, patientId)}/statement/preview`,
    {},
    "Could not build the statement preview.",
  );
  return {
    patientId: body.patient_id,
    patientName: body.patient_name || null,
    toEmail: typeof body.to_email === "string" ? body.to_email : null,
    balanceCents: num(body.balance_cents) ?? 0,
    lines: Array.isArray(body.lines)
      ? body.lines.map((l) => ({
          dos: (l && l.dos) || null,
          balanceCents: num(l && l.balance_cents) ?? 0,
        }))
      : [],
    // Only an explicit true may send; a body we cannot read never enables the button.
    canSend: body.can_send === true,
    reason: body.reason || null,
    lastSend: body.last_send ? normalizeSend(body.last_send) : null,
  };
}

// POST …/statement/send {confirm: true, expected_email}. Errors carry `status` and
// `body` (409 changed address → body.to_email; 409 recent / 502 → body.send).
export async function sendStatement(practiceId, patientId, expectedEmail) {
  try {
    const body = await request(
      `${patientBase(practiceId, patientId)}/statement/send`,
      post({ confirm: true, expected_email: expectedEmail }),
      "The statement could not be sent.",
    );
    return body.send ? normalizeSend(body.send) : null;
  } finally {
    // A refused or failed send can still have written a row (502 writes `failed`).
    invalidatePayStatus(practiceId);
  }
}

export async function fetchSends(practiceId, patientId) {
  const body = await request(
    `${patientBase(practiceId, patientId)}/statement/sends`,
    {},
    "Could not load the statements sent.",
  );
  if (!Array.isArray(body.sends)) {
    throw new Error(
      "The response could not be read, so this list may be incomplete. Try again.",
    );
  }
  return body.sends.map(normalizeSend);
}

export function normalizeSend(raw) {
  const s = raw || {};
  return {
    id: s.id,
    patientId: s.patient_id,
    sentBy: s.sent_by || null,
    sentAt: s.sent_at || null,
    toEmail: s.to_email || null,
    balanceCents: num(s.balance_cents),
    expiresAt: s.expires_at || null,
    status: s.status || null,
    error: s.error || null,
  };
}

// ---- card on file, charge, refund (A's phase 2; docs/collections.md "Biller API") ----
//
// The charge and the refund POST NOTHING to the ledger: the signed webhook posts when
// Stripe confirms. Each is called from exactly one confirm button, carrying a
// `request_id` that dialog generated once — Stripe's idempotency key, so a
// double-submitted confirm is one charge. No card field exists anywhere here: Atlas
// sees brand, last four and expiry, read back from Stripe.

// 16–64 of [A-Za-z0-9-] (stripe_service._REQUEST_ID). A UUID fits.
export function newRequestId() {
  // `globalThis.crypto`, not `window.crypto`: the same object in a browser, and
  // present under Node (and so in jsdom-less test environments) too.
  const c = typeof globalThis !== "undefined" ? globalThis.crypto : null;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  if (c && typeof c.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    c.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  }
  // No Web Crypto means no charge: an idempotency key must not be guessable-by-
  // collision, and Math.random is not good enough to stand in.
  throw new Error(
    "This browser cannot make a safe request id. Use a current browser.",
  );
}

export function normalizeCard(raw) {
  if (!raw || typeof raw !== "object") return null;
  return {
    id: raw.id,
    brand: raw.card_brand || null,
    last4: raw.card_last4 || null,
    exp: raw.card_exp || null,
    consentTextVersion: num(raw.consent_text_version),
    consentedAt: raw.consented_at || null,
    consentedVia: raw.consented_via || null,
    revokedAt: raw.revoked_at || null,
    live: raw.live === true,
  };
}

const normalizeVisit = (v) => ({
  claimId: v && v.claim_id,
  dos: (v && v.dos) || null,
  balanceCents: num(v && v.balance_cents) ?? 0,
  reason: (v && v.reason) || null,
});

// GET …/card → what the charge confirm shows.
export async function fetchCard(practiceId, patientId) {
  const body = await request(
    `${patientBase(practiceId, patientId)}/card`,
    {},
    "Could not load the card on file.",
  );
  return {
    patientId: body.patient_id,
    // Only an explicit true: a body we cannot read never offers a charge.
    enabled: body.enabled === true,
    card: normalizeCard(body.card),
    consentText:
      typeof body.consent_text === "string" ? body.consent_text : null,
    balanceCents: num(body.balance_cents) ?? 0,
    chargeableCents: num(body.chargeable_cents) ?? 0,
    eligible: Array.isArray(body.eligible)
      ? body.eligible.map(normalizeVisit)
      : [],
    held: Array.isArray(body.held) ? body.held.map(normalizeVisit) : [],
    rules: normalizeRules(body.rules),
  };
}

// POST …/card/charge. Errors carry `status` and `body` (409 card changed → body.card;
// 409 unposted → body.unposted_cents; 422 → chargeable_cents / held; 402 decline_code).
export function chargeCard(
  practiceId,
  patientId,
  { amountCents, consentId, expectedLast4, requestId },
) {
  return request(
    `${patientBase(practiceId, patientId)}/card/charge`,
    post({
      confirm: true,
      amount_cents: amountCents,
      consent_id: consentId,
      expected_last4: expectedLast4,
      request_id: requestId,
    }),
    "The charge could not be made.",
  );
}

export async function revokeCard(practiceId, patientId) {
  const body = await request(
    `${patientBase(practiceId, patientId)}/card/revoke`,
    post({ confirm: true }),
    "Could not remove the card.",
  );
  return normalizeCard(body.card);
}

// A's `refund_requested` (O13): true from the moment a refund is asked of Stripe
// until its reversal posts. `notes` is no longer on the wire — it was a biller's free
// text about a patient, carried only so the UI could detect this — and the earlier
// note-matching fallback is gone with it: Atlas ships one image, so the frontend
// cannot meet a backend that predates the field.
export async function fetchCardPayments(practiceId, patientId) {
  const body = await request(
    `${patientBase(practiceId, patientId)}/payments`,
    {},
    "Could not load card payments.",
  );
  if (!Array.isArray(body.payments)) {
    throw new Error(
      "The response could not be read, so this list may be incomplete. Try again.",
    );
  }
  return body.payments.map((p) => ({
    id: p.id,
    amountCents: num(p.amount_cents) ?? 0,
    paymentDate: p.payment_date || null,
    status: p.status || null,
    refundableCents: num(p.refundable_cents) ?? 0,
    // The refundable amount does not fall until Stripe's webhook lands, so without
    // this a reloaded page shows a full refundable amount for a refund already under
    // way, and a second confirmed refund would be a second refund to the patient.
    refundRequested: p.refund_requested === true,
  }));
}

export function refundPayment(
  practiceId,
  patientId,
  paymentId,
  { amountCents, reason, requestId },
) {
  return request(
    `${patientBase(practiceId, patientId)}/payments/${encodeURIComponent(paymentId)}/refund`,
    post({
      confirm: true,
      amount_cents: amountCents,
      reason,
      request_id: requestId,
    }),
    "The refund could not be made.",
  );
}

// Why a patient cannot be charged, from the server's own figures — or null when they
// can be. Ordered on the EVIDENCE rather than the sign of the balance: a zero balance
// with no lines at all is a patient who owes nothing (a refund-due row, or one whose
// balance just went to zero), and telling them "a credit cancels what is owed" would
// assert a credit that need not exist. `statements_service.balances` only returns
// patients owing more than half a penny, so the netting case cannot arrive with a
// zero balance — it is kept for a balance that nets down, not away.
export function chargeRefusal(summary) {
  if (!summary) return null;
  if (summary.chargeableCents > 0) return null;
  if (!summary.eligible.length && !summary.held.length) {
    return "This patient has no balance to charge.";
  }
  if (summary.balanceCents <= 0) {
    return "This patient owes nothing overall — a credit on one visit cancels what is owed on another, so there is nothing to charge.";
  }
  if (summary.eligible.length > 0) {
    return "What this patient owes nets to nothing chargeable once their credits are counted.";
  }
  return "Nothing this practice's rules allow is due — every visit they owe for is held, with the reason on each.";
}

// ---- practice settings (Practice Details "Collections" card) ----
//
// GET …/collections/settings (A): whether card payments are configured, whether the
// practice has its own Stripe account, the wording patients see, and the server's own
// list of send errors that may still have delivered. Cached per practice for the life
// of the tab: it changes only with a deploy or a practice edit.

const settingsCache = new Map(); // practiceId -> promise
const settingsSubscribers = new Set();

// Mounted cards subscribe so an invalidate reaches them. Without this the cache is
// dropped and every card keeps the object it already has: their effects are keyed on
// practiceId, which a rename does not change, so nothing reruns. `useCapabilities`
// documents the same trap and solves it the same way.
export function subscribeSettings(fn) {
  settingsSubscribers.add(fn);
  return () => settingsSubscribers.delete(fn);
}

// Drop the cached settings WITHOUT fetching or notifying. For tests (so one test's
// settings cannot be read by the next) and for a sign-out. Anything that changes what
// the settings say wants `invalidateSettings`, which re-reads them.
export function resetSettingsCache() {
  settingsCache.clear();
}

// Drop the cached settings and fetch them again, then tell every mounted card. Call
// it after anything that changes what the settings SAY — renaming the practice
// changes the consent wording, which quotes the practice name.
export function invalidateSettings(practiceId) {
  settingsCache.delete(practiceId);
  if (practiceId == null) {
    settingsSubscribers.forEach((fn) => fn(practiceId));
    return;
  }
  fetchSettings(practiceId)
    .catch(() => {
      // Subscribers show their own absent state; nothing to do here.
    })
    .finally(() => {
      settingsSubscribers.forEach((fn) => fn(practiceId));
    });
}

export function fetchSettings(practiceId) {
  const hit = settingsCache.get(practiceId);
  if (hit) return hit;
  const promise = request(
    `${base(practiceId)}/settings`,
    {},
    "Could not load the collections settings.",
  ).then((body) => {
    const stripe = body.stripe && typeof body.stripe === "object" ? body.stripe : {};
    return {
      stripe: {
        enabled: stripe.enabled === true,
        connectedAccount: stripe.connected_account === true,
      },
      consentText:
        typeof body.consent_text === "string" ? body.consent_text : null,
      consentTextVersion: num(body.consent_text_version),
      emailWordingVersion: num(body.email_wording_version),
      // The server's own list wins over the copy below, so the two cannot drift.
      ambiguousSendErrors: Array.isArray(body.ambiguous_send_errors)
        ? body.ambiguous_send_errors.filter((e) => typeof e === "string")
        : null,
    };
  });
  promise.catch(() => {
    if (settingsCache.get(practiceId) === promise) settingsCache.delete(practiceId);
  });
  settingsCache.set(practiceId, promise);
  return promise;
}

// ---- practice rules (Practice Details "Collections" card) ----

export async function fetchRules(practiceId) {
  const body = await request(
    `${base(practiceId)}/rules`,
    {},
    "Could not load the collection rules.",
  );
  if (!body.rules || typeof body.rules !== "object") {
    throw new Error("The collection rules could not be read. Try again.");
  }
  return normalizeRules(body.rules);
}

// PUT …/rules with only the keys given. Returns the stored rules.
export async function saveRules(practiceId, patch) {
  const payload = {};
  if ("chargeNoShowFee" in patch)
    payload.charge_no_show_fee = patch.chargeNoShowFee;
  if ("chargeOverdue" in patch) payload.charge_overdue = patch.chargeOverdue;
  if ("cadenceDays" in patch) payload.cadence_days = patch.cadenceDays;
  if ("overdueDays" in patch) payload.overdue_days = patch.overdueDays;
  if ("noShowFeeCents" in patch) payload.no_show_fee_cents = patch.noShowFeeCents;
  if ("noShowExemptSameDayReschedule" in patch)
    payload.no_show_exempt_same_day_reschedule =
      patch.noShowExemptSameDayReschedule;
  if ("statementFromName" in patch)
    payload.statement_from_name = patch.statementFromName || null;
  if ("replyTo" in patch) payload.reply_to = patch.replyTo || null;
  const body = await request(
    `${base(practiceId)}/rules`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
    "Could not save the collection rules.",
  );
  return normalizeRules(body.rules);
}

// ---- pay status, for the chips on Statements and the patient drawer ----
//
// One request per practice however many chips are mounted: the Statements table
// mounts one per row. Cached briefly and dropped after a send from this tab.

const PAY_STATUS_TTL_MS = 30000;
const payStatusCache = new Map(); // practiceId -> {at, promise}

export function invalidatePayStatus(practiceId) {
  payStatusCache.delete(practiceId);
}

export function fetchPayStatus(practiceId, now = Date.now()) {
  const hit = payStatusCache.get(practiceId);
  if (hit && now - hit.at < PAY_STATUS_TTL_MS) return hit.promise;
  const promise = request(
    `${base(practiceId)}/pay-status`,
    {},
    "Could not load statement statuses.",
  ).then((body) => {
    const patients =
      body.patients && typeof body.patients === "object" ? body.patients : {};
    return patients;
  });
  // A failure is not cached: the next mount asks again.
  promise.catch(() => {
    if (payStatusCache.get(practiceId)?.promise === promise) {
      payStatusCache.delete(practiceId);
    }
  });
  payStatusCache.set(practiceId, { at: now, promise });
  return promise;
}

// ---- display helpers ----

// Server timestamps are UTC with a trailing Z; a bare one is still read as UTC.
export function parseServerTime(value) {
  if (!value) return null;
  const text = String(value);
  const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(text.slice(10));
  const when = new Date(hasZone ? text : `${text}Z`);
  return Number.isNaN(when.getTime()) ? null : when;
}

export function formatWhen(value) {
  const when = parseServerTime(value);
  if (!when) return "—";
  return when.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// A calendar date (YYYY-MM-DD) is not a moment: format it without a time zone, or
// "2026-09-19" renders as the 18th west of UTC.
export function formatDay(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
  if (!m) return value || "—";
  const when = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return when.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}
